import React, { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Loader } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUpload, 
  faVideo, 
  faMagicWandSparkles, 
  faTimes,
  faPlay,
  faDownload,
  faFileVideo
} from '@fortawesome/free-solid-svg-icons';
import { useNotification } from '../../../context/NotificationContext';
import { getApiUrl } from '../../../config/api';
import { LibraryItem } from '../../../types';
import { VideoLibraryModal } from './VideoLibraryModal';

interface UpscaleTabProps {
  projectId?: string;
  initialVideoUrl?: string;
  onUpscaleComplete?: (result: UpscaleResult) => void;
  onCancel?: () => void;
  onUseVideo?: (videoUrl: string, prompt: string, duration: number) => void;
}

interface UpscaleResult {
  originalUrl: string;
  upscaledUrl: string;
  projectId: string;
}

interface UpscaleProgress {
  status: 'idle' | 'uploading' | 'processing' | 'completed' | 'error';
  progress: number;
  message: string;
}

interface SourceVideo {
  file?: File;
  url?: string;
  preview?: string;
  name?: string;
}

export const UpscaleTab: React.FC<UpscaleTabProps> = ({
  projectId,
  initialVideoUrl,
  onUpscaleComplete,
  onCancel,
  onUseVideo
}) => {
  const { t } = useTranslation('aiGenerator');
  const [sourceVideo, setSourceVideo] = useState<SourceVideo>({});
  const [upscaleProgress, setUpscaleProgress] = useState<UpscaleProgress>({
    status: 'idle',
    progress: 0,
    message: ''
  });
  const [result, setResult] = useState<UpscaleResult | null>(null);
  const [activeSource, setActiveSource] = useState<'upload' | 'library'>('upload');
  const [isLibraryModalOpen, setIsLibraryModalOpen] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showNotification } = useNotification();

  // Auto-load video from initialVideoUrl
  React.useEffect(() => {
    if (initialVideoUrl) {
      setSourceVideo({
        url: initialVideoUrl,
        preview: initialVideoUrl,
        name: 'Selected Video'
      });
      
      setUpscaleProgress({
        status: 'idle',
        progress: 0,
        message: 'Video loaded from library. Ready to upscale!'
      });
    }
  }, [initialVideoUrl]);

  // Handle video file upload
  const handleVideoUpload = useCallback((file: File) => {
    if (!file.type.startsWith('video/')) {
      showNotification({
        title: t('upscale.errors.invalidFile'),
        message: t('upscale.errors.invalidFile'),
        type: 'error'
      });
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    setSourceVideo({
      file,
      preview: previewUrl,
      name: file.name
    });

    setUpscaleProgress({
      status: 'idle',
      progress: 0,
      message: t('upscale.video.loaded')
    });
  }, [showNotification, t]);

  // Handle video from library selection
  const handleLibraryVideo = useCallback((libraryItem: LibraryItem) => {
    if (!libraryItem.url) {
      showNotification({
        title: t('upscale.errors.noVideoUrl'),
        message: t('upscale.errors.noVideoUrl'),
        type: 'error'
      });
      return;
    }

    setSourceVideo({
      url: libraryItem.url,
      preview: libraryItem.url,
      name: libraryItem.name || 'Library Video'
    });

    setUpscaleProgress({
      status: 'idle',
      progress: 0,
      message: t('upscale.video.loadedFromLibrary')
    });

    setIsLibraryModalOpen(false);
  }, [showNotification, t]);

  // Start upscaling process
  const handleUpscale = useCallback(async () => {
    if (!sourceVideo.file && !sourceVideo.url) {
      showNotification({
        title: t('upscale.errors.selectFirst'),
        message: t('upscale.errors.selectFirst'),
        type: 'error'
      });
      return;
    }

    try {
      setUpscaleProgress({
        status: 'uploading',
        progress: 10,
        message: t('upscale.video.checkingDuration')
      });

      // Check video duration first
      const videoElement = document.createElement('video');
      const checkDuration = new Promise<number>((resolve, reject) => {
        videoElement.addEventListener('loadedmetadata', () => {
          resolve(videoElement.duration);
        });
        
        videoElement.addEventListener('error', () => {
          reject(new Error('Failed to load video metadata'));
        });
        
        if (sourceVideo.file) {
          videoElement.src = URL.createObjectURL(sourceVideo.file);
        } else if (sourceVideo.url) {
          videoElement.src = sourceVideo.url;
        }
      });

      const duration = await checkDuration;
      
      // Validate duration (10 seconds = 10.0 seconds)
      if (duration > 10) {
        setUpscaleProgress({
          status: 'error',
          progress: 0,
          message: t('upscale.video.tooLong', { duration: Math.round(duration) })
        });
        
        showNotification({
          title: t('upscale.video.tooLong', { duration: Math.round(duration) }),
          message: t('upscale.video.tooLongMessage', { duration: Math.round(duration) }),
          type: 'error'
        });
        return;
      }

      setUpscaleProgress({
        status: 'uploading',
        progress: 20,
        message: t('upscale.video.durationValidated')
      });

      let videoUrl = sourceVideo.url;

      // If it's a file, upload it first
      if (sourceVideo.file) {
        setUpscaleProgress({
          status: 'uploading',
          progress: 30,
          message: t('upscale.video.uploading')
        });

        const formData = new FormData();
        formData.append('video', sourceVideo.file);
        
        const uploadResponse = await fetch(`${getApiUrl('')}/api/upload/video`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          },
          body: formData
        });

        if (!uploadResponse.ok) {
          throw new Error(t('upscale.errors.uploadFailed'));
        }

        const uploadResult = await uploadResponse.json();
        videoUrl = uploadResult.url;
      }

      setUpscaleProgress({
        status: 'processing',
        progress: 50,
        message: t('upscale.video.processing')
      });

      // Call upscale API
      const upscaleResponse = await fetch(`${getApiUrl('')}/video-generation/upscale`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
        body: JSON.stringify({
          source_video_url: videoUrl,
          provider: 'runway',
          operation: "upscale",
          duration: duration
        })
      });

      if (!upscaleResponse.ok) {
        throw new Error('Failed to start upscaling process');
      }

      const upscaleResult = await upscaleResponse.json();

      if (upscaleResult.success) {
        setUpscaleProgress({
          status: 'completed',
          progress: 100,
          message: t('upscale.video.success')
        });

        const finalResult: UpscaleResult = {
          originalUrl: videoUrl!,
          upscaledUrl: upscaleResult.data.s3Url,
          projectId: upscaleResult.data.projectId
        };

        setResult(finalResult);
        onUpscaleComplete?.(finalResult);
        
        // showNotification({
        //   title: 'Success',
        //   message: 'Video upscaled successfully!',
        //   type: 'success'
        // });
      } else {
        throw new Error(upscaleResult.error || t('upscale.errors.upscaleFailed'));
      }

    } catch (error: any) {
      setUpscaleProgress({
        status: 'error',
        progress: 0,
        message: error.message || t('upscale.errors.upscaleFailed')
      });
      
      showNotification({
        title: t('upscale.errors.upscaleFailed'),
        message: error.message || t('upscale.errors.upscaleFailed'),
        type: 'error'
      });
    }
  }, [sourceVideo, showNotification, onUpscaleComplete, t]);

  // Handle adding upscaled video to timeline
  const handleUseVideo = useCallback(() => {
    if (result && onUseVideo) {
      // Use the upscaled video URL, duration from original, and descriptive prompt
      onUseVideo(
        result.upscaledUrl, 
        `Upscaled video from: ${result.originalUrl}`,
        8.0 // Default duration, could be extracted from video metadata if needed
      );
      onCancel?.(); // Close modal after adding to timeline
    }
  }, [result, onUseVideo, onCancel]);
  // Reset to initial state
  const handleReset = useCallback(() => {
    setSourceVideo({});
    setUpscaleProgress({
      status: 'idle',
      progress: 0,
      message: ''
    });
    setResult(null);
  }, []);

  // Handle file input change
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleVideoUpload(files[0]);
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      {/* Source Video Selection */}
      {!sourceVideo.file && !sourceVideo.url && (
        <div>
          {/* Source Selection Tabs */}
          <div style={{
            display: 'flex',
            gap: '8px',
            marginBottom: '20px',
            borderBottom: '1px solid #333',
            paddingBottom: '12px'
          }}>
            <button
              onClick={() => setActiveSource('upload')}
              style={{
                padding: '8px 16px',
                backgroundColor: activeSource === 'upload' ? '#8b5cf6' : 'transparent',
                color: activeSource === 'upload' ? 'white' : '#ccc',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s ease'
              }}
            >
              <FontAwesomeIcon icon={faUpload} style={{ marginRight: '8px' }} />
              {t('upscale.tabs.upload')}
            </button>
            <button
              onClick={() => setActiveSource('library')}
              style={{
                padding: '8px 16px',
                backgroundColor: activeSource === 'library' ? '#8b5cf6' : 'transparent',
                color: activeSource === 'library' ? 'white' : '#ccc',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s ease'
              }}
            >
              <FontAwesomeIcon icon={faVideo} style={{ marginRight: '8px' }} />
              {t('upscale.tabs.library')}
            </button>
          </div>

          {/* Upload Content */}
          {activeSource === 'upload' && (
            <div style={{
              border: '2px dashed #444',
              borderRadius: '12px',
              padding: '40px',
              textAlign: 'center',
              backgroundColor: '#1a1a1a'
            }}>
              <FontAwesomeIcon 
                icon={faFileVideo} 
                style={{ fontSize: '48px', color: '#666', marginBottom: '16px' }} 
              />
              <h3 style={{ color: '#fff', margin: '0 0 8px 0' }}>{t('upscale.upload.title')}</h3>
              <p style={{ color: '#999', margin: '0 0 20px 0' }}>
                {t('upscale.upload.description')}
              </p>
              <Button
                onClick={() => fileInputRef.current?.click()}
                leftSection={<FontAwesomeIcon icon={faUpload} />}
                style={{
                  background: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
                  border: 'none',
                  borderRadius: '8px'
                }}
              >
                {t('upscale.upload.selectButton')}
              </Button>
            </div>
          )}

          {/* Library Content */}
          {activeSource === 'library' && (
            <div>
              <h3 style={{ color: '#fff', marginBottom: '16px' }}>{t('upscale.library.title')}</h3>
              <div style={{
                border: '1px solid #333',
                borderRadius: '12px',
                padding: '20px',
                backgroundColor: '#1a1a1a',
                maxHeight: '400px',
                overflow: 'hidden'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '15px'
                }}>
                  <h4 style={{ color: '#8b5cf6', margin: '0' }}>{t('upscale.library.videosAvailable')}</h4>
                  <Button
                    onClick={() => setIsLibraryModalOpen(true)}
                    size="sm"
                    style={{
                      background: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
                      border: 'none',
                      borderRadius: '8px'
                    }}
                  >
                    <FontAwesomeIcon icon={faVideo} style={{ marginRight: '8px' }} />
                    {t('upscale.library.browseButton')}
                  </Button>
                </div>
                <p style={{ color: '#999', margin: '0', fontSize: '14px' }}>
                  {t('upscale.library.description')}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Video Preview & Processing */}
      {(sourceVideo.file || sourceVideo.url) && !result && (
        <div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <h3 style={{ color: '#fff', margin: '0' }}>
              <FontAwesomeIcon icon={faVideo} style={{ marginRight: '8px' }} />
              {t('upscale.video.sourceVideo', { name: sourceVideo.name })}
            </h3>
            <button
              onClick={handleReset}
              style={{
                padding: '8px 12px',
                backgroundColor: '#333',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              <FontAwesomeIcon icon={faTimes} style={{ marginRight: '8px' }} />
              {t('upscale.video.chooseAnother')}
            </button>
          </div>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 300px',
            gap: '20px',
            marginBottom: '20px'
          }}>
            {/* Video Preview */}
            <div>
              <video
                src={sourceVideo.preview}
                controls
                style={{
                  width: '100%',
                  maxHeight: '300px',
                  borderRadius: '8px'
                }}
              />
            </div>

            {/* Upscale Info & Controls */}
            <div style={{
              padding: '20px',
              backgroundColor: '#222',
              borderRadius: '8px',
              border: '1px solid #333'
            }}>
              <h4 style={{ color: '#fff', marginTop: '0' }}>{t('upscale.details.title')}</h4>
              <div style={{ marginBottom: '20px' }}>
                <div style={{ marginBottom: '8px' }}>
                  <span style={{ color: '#999' }}>{t('upscale.details.resolution')}</span>
                  <strong style={{ color: '#fff', marginLeft: '8px' }}>4X</strong>
                </div>
                <div style={{ marginBottom: '8px' }}>
                  <span style={{ color: '#999' }}>{t('upscale.details.provider')}</span>
                  <strong style={{ color: '#fff', marginLeft: '8px' }}>{t('upscale.details.runway')}</strong>
                </div>
                <div style={{ marginBottom: '8px' }}>
                  <span style={{ color: '#999' }}>{t('upscale.details.processingTime')}</span>
                  <strong style={{ color: '#fff', marginLeft: '8px' }}>{t('upscale.details.timingEstimate')}</strong>
                </div>
              </div>

              <Button
                onClick={handleUpscale}
                disabled={upscaleProgress.status === 'uploading' || upscaleProgress.status === 'processing'}
                style={{
                  width: '100%',
                  background: upscaleProgress.status === 'uploading' || upscaleProgress.status === 'processing' 
                    ? '#666' 
                    : 'linear-gradient(135deg, #7c3aed, #a855f7)',
                  border: 'none',
                  borderRadius: '8px'
                }}
                size="lg"
                leftSection={
                  upscaleProgress.status === 'uploading' || upscaleProgress.status === 'processing' ? (
                    <Loader size="sm" />
                  ) : (
                    <FontAwesomeIcon icon={faMagicWandSparkles} />
                  )
                }
              >
                {upscaleProgress.status === 'uploading' || upscaleProgress.status === 'processing' 
                  ? t('upscale.buttons.processing')
                  : t('upscale.buttons.upscale')
                }
              </Button>
            </div>
          </div>

          {/* Progress Indicator */}
          {upscaleProgress.status !== 'idle' && (
            <div style={{
              padding: '20px',
              backgroundColor: '#1a1a1a',
              borderRadius: '8px',
              border: '1px solid #333'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '10px'
              }}>
                <h4 style={{ color: '#fff', margin: '0' }}>{t('upscale.status.title')}</h4>
                <span style={{ color: '#8b5cf6', fontWeight: 'bold' }}>
                  {t('upscale.status.progress', { percent: upscaleProgress.progress })}
                </span>
              </div>
              
              <div style={{
                width: '100%',
                height: '8px',
                backgroundColor: '#333',
                borderRadius: '4px',
                overflow: 'hidden',
                marginBottom: '10px'
              }}>
                <div
                  style={{
                    width: `${upscaleProgress.progress}%`,
                    height: '100%',
                    backgroundColor: upscaleProgress.status === 'error' 
                      ? '#ef4444' 
                      : upscaleProgress.status === 'completed'
                      ? '#10b981'
                      : '#8b5cf6',
                    transition: 'width 0.3s ease'
                  }}
                />
              </div>
              
              <p style={{
                color: upscaleProgress.status === 'error' 
                  ? '#ef4444' 
                  : upscaleProgress.status === 'completed'
                  ? '#10b981'
                  : '#ccc',
                margin: '0'
              }}>
                {upscaleProgress.message}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Results */}
      {result && (
        <div>
          <h3 style={{ 
            color: '#fff', 
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center'
          }}>
            <FontAwesomeIcon icon={faMagicWandSparkles} style={{ marginRight: '8px' }} />
            {t('upscale.results.title')}
          </h3>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '20px',
            marginBottom: '20px'
          }}>
            {/* Original */}
            <div>
              <h4 style={{ color: '#fff' }}>{t('upscale.results.original')}</h4>
              <video
                src={result.originalUrl}
                controls
                style={{
                  width: '100%',
                  borderRadius: '8px'
                }}
              />
            </div>

            {/* Upscaled */}
            <div>
              <h4 style={{ color: '#fff' }}>{t('upscale.results.upscaled')}</h4>
              <video
                src={result.upscaledUrl}
                controls
                style={{
                  width: '100%',
                  borderRadius: '8px'
                }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            {onUseVideo && (
              <Button
                onClick={handleUseVideo}
                leftSection={<FontAwesomeIcon icon={faPlay} />}
                size="lg"
                style={{
                  background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                  border: 'none',
                  borderRadius: '8px'
                }}
              >
                {t('upscale.buttons.addToTimeline')}
              </Button>
            )}
            <Button
              onClick={() => window.open(result.upscaledUrl, '_blank')}
              leftSection={<FontAwesomeIcon icon={faDownload} />}
              size="lg"
              style={{
                background: 'linear-gradient(135deg, #10b981, #059669)',
                border: 'none',
                borderRadius: '8px'
              }}
            >
              {t('upscale.buttons.download')}
            </Button>
            <Button
              onClick={handleReset}
              variant="outline"
              size="lg"
              style={{
                borderColor: '#666',
                color: '#ccc',
                borderRadius: '8px'
              }}
            >
              {t('upscale.buttons.upscaleAnother')}
            </Button>
          </div>
        </div>
      )}

      {/* Cancel Button - always visible */}
      {onCancel && (
        <div style={{ 
          marginTop: '20px', 
          textAlign: 'center',
          borderTop: '1px solid #333',
          paddingTop: '20px'
        }}>
          <button
            onClick={onCancel}
            style={{
              padding: '12px 24px',
              border: '1px solid rgba(255,255,255,0.2)',
              backgroundColor: 'transparent',
              color: 'white',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            {t('common.cancel')}
          </button>
        </div>
      )}

      {/* Hidden file input */}
      <input
        type="file"
        accept="video/*"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {/* Video Library Modal */}
      <VideoLibraryModal
        isOpen={isLibraryModalOpen}
        onClose={() => setIsLibraryModalOpen(false)}
        onVideoSelected={handleLibraryVideo}
        projectId={projectId || ''}
      />
    </div>
  );
};